/**
 * <s-sticky-buy> — mobile sticky purchase bar for the product page.
 *
 * The bar's button is NOT a form submitter. It clicks the main purchase button
 * ([data-main-add-button] in the same form), so every app that listens on that button
 * (STOQ preorder handling, Warnify confirmation) and s-product-form see one purchase path.
 *
 * It also mirrors the main button's presentation, because STOQ restyles only the first
 * button in the form: label text, disabled state and STOQ's restock-rocket classes
 * (which carry the preorder colours) are copied whenever the main button changes.
 * Re-rendered with the 'product-buy' partial, so it reconnects on every variant change.
 */
class StickyBuy extends HTMLElement {
  #observer = null;

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    // STOQ may relabel the main button after a short delay, so observe rather than read once.
    queueMicrotask(() => {
      this.#watch();
      this.#watchScroll();
    });
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.#observer?.disconnect();
    this.#observer = null;
    this.#visibility?.disconnect();
    this.#visibility = null;
  }

  #visibility = null;

  // Show the bar only once the main button has scrolled above the screen (CSS hides it otherwise).
  // No main button (sold out) means no bar.
  #watchScroll() {
    const main = this.#main;
    if (!main || !('IntersectionObserver' in window)) return;
    this.#visibility = new IntersectionObserver(([entry]) => {
      const scrolledPast = !entry.isIntersecting && entry.boundingClientRect.bottom <= 0;
      this.toggleAttribute('data-visible', scrolledPast);
    });
    this.#visibility.observe(main);
  }

  get #main() {
    return this.closest('form')?.querySelector('[data-main-add-button]') ?? null;
  }

  get #proxy() {
    return this.querySelector('[data-sticky-add]');
  }

  #onClick = (event) => {
    const proxy = this.#proxy;
    if (!proxy || !(event.target instanceof Element) || !proxy.contains(event.target)) return;
    event.preventDefault();
    const main = this.#main;
    if (!main || main.disabled || main.getAttribute('aria-disabled') === 'true') return;
    main.click();
  };

  #watch() {
    const main = this.#main;
    if (!main || !this.#proxy) return;
    this.#sync();
    this.#observer = new MutationObserver(() => this.#sync());
    this.#observer.observe(main, { attributes: true, attributeFilter: ['class', 'disabled', 'aria-disabled', 'style'], childList: true, characterData: true, subtree: true });
  }

  #sync() {
    const main = this.#main;
    const proxy = this.#proxy;
    if (!main || !proxy) return;

    const label = (main.querySelector('span')?.textContent || main.textContent || '').trim();
    const proxyLabel = proxy.querySelector('span') || proxy;
    if (label && proxyLabel.textContent.trim() !== label) proxyLabel.textContent = label;

    // Copy STOQ's classes (restock-rocket-*) so the preorder colours apply here too; drop stale ones.
    [...proxy.classList].filter((c) => c.startsWith('restock-rocket')).forEach((c) => {
      if (!main.classList.contains(c)) proxy.classList.remove(c);
    });
    [...main.classList].filter((c) => c.startsWith('restock-rocket')).forEach((c) => proxy.classList.add(c));

    const disabled = main.disabled || main.getAttribute('aria-disabled') === 'true';
    proxy.disabled = disabled;
    if (disabled) proxy.setAttribute('aria-disabled', 'true');
    else proxy.removeAttribute('aria-disabled');
  }
}

if (!customElements.get('s-sticky-buy')) customElements.define('s-sticky-buy', StickyBuy);
